"use client";

import { useEffect, useRef, useState } from "react";
import type { MenuGroup, MenuLink } from "@/features/site-content/menus";
import { IconChevronDown } from "@/components/ui/icons";
import { MenuAnchor, MenuLabel } from "./menu-link";
import { useCurrentPath } from "./use-current-path";

type Labels = { mainAria: string; allPrefix: string; newTab: string };

// Açılır menüler tıklama ve klavyeyle çalışır; fareyle üzerine gelmek yalnızca kolaylıktır. Odak menüden çıkınca
// (Tab ile sonraki öğeye) menü kapanır. Geçerli sayfa yalnızca aria-current ile bildirilir (ekran okuyucu "geçerli
// sayfa" der; ek gizli metin aynı bilgiyi iki kez okutuyordu). Öğeler Site Yönetimi → Menüler'den (MenuAnchor: dış / yeni
// sekme bağlantıları); açık menü öğe kimliğiyle izlenir (aynı yazılı iki öğe karışmaz).
export function DesktopNav({ items, labels }: { items: MenuGroup[]; labels: Labels }) {
  const [open, setOpen] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const pathname = useCurrentPath();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const button = navRef.current?.querySelector<HTMLButtonElement>(`[data-menu="${open}"]`);
        setOpen(null);
        button?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const isActive = ({ href, external }: MenuLink) =>
    !external && (href === "/" || href === "/en" ? pathname === href : pathname === href || pathname.startsWith(`${href}/`));

  return (
    <nav ref={navRef} aria-label={labels.mainAria} className="hidden lg:block">
      <ul className="flex items-center gap-1">
        {items.map((item) => {
          const active = isActive(item) || item.children?.some((c) => isActive(c));
          const topClass = `relative inline-flex h-11 items-center rounded-md px-3 text-[0.95rem] font-semibold transition-colors hover:bg-primary-soft after:absolute after:inset-x-3 after:bottom-1 after:h-0.5 after:rounded-full ${active ? "text-ink after:bg-accent" : "text-ink-muted after:bg-transparent"}`;
          if (!item.children) {
            return (
              <li key={item.key}>
                <MenuAnchor link={item} newTabLabel={labels.newTab} current={active} className={topClass} />
              </li>
            );
          }
          const expanded = open === item.key;
          const menuId = `menu-${item.key}`;
          return (
            <li
              key={item.key}
              className="relative"
              onMouseEnter={() => {
                if (closeTimer.current) clearTimeout(closeTimer.current);
                setOpen(item.key);
              }}
              onMouseLeave={() => {
                closeTimer.current = setTimeout(() => setOpen((cur) => (cur === item.key ? null : cur)), 120);
              }}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen((cur) => (cur === item.key ? null : cur));
              }}
            >
              <button
                type="button"
                data-menu={item.key}
                aria-expanded={expanded}
                aria-controls={menuId}
                onClick={() => setOpen(expanded ? null : item.key)}
                className={`${topClass} gap-1`}
              >
                <MenuLabel link={item} />
                <IconChevronDown size={16} className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
              </button>
              <div
                id={menuId}
                hidden={!expanded}
                className="absolute top-full left-1/2 z-40 w-72 -translate-x-1/2 pt-2"
              >
                <ul className="rounded-lg bg-surface p-2 shadow-raised ring-1 ring-line">
                  <li>
                    <MenuAnchor
                      link={item}
                      newTabLabel={labels.newTab}
                      onClick={() => setOpen(null)}
                      className="flex min-h-11 items-center rounded-md px-3 py-2 text-sm font-bold text-cta-text hover:bg-cta-soft"
                    >
                      {labels.allPrefix}
                      <MenuLabel link={item} />
                    </MenuAnchor>
                  </li>
                  {item.children.map((child) => (
                    <li key={child.key}>
                      <MenuAnchor
                        link={child}
                        newTabLabel={labels.newTab}
                        onClick={() => setOpen(null)}
                        current={isActive(child)}
                        className="block rounded-md px-3 py-2.5 text-[0.95rem] font-medium text-ink hover:bg-primary-soft aria-[current=page]:bg-primary-soft"
                        after={child.description && <span className="block text-xs font-normal text-ink-muted">{child.description}</span>}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
